/**
 * The guarantee a host puts down before they can take bookings that charge a
 * customer up front -- and what it lets them carry.
 *
 * Every figure here is computed server-side (HostExposureCalculator) rather
 * than reassembled on screen, so the ceiling a host is shown is literally the
 * one the booking gate enforces. A host told they can take a 6.000 EUR job
 * and then refused one is a host who stops trusting the number.
 */
export interface IHostDeposit {
  /** False means the platform is not asking anybody for a deposit right now -- say so rather than showing an empty account as if something were missing. */
  required: boolean;

  balance: number;
  /** Everything ever paid in, ignoring what has been taken out -- the figure a host recognises as "what I put in" when they ask why they have less. */
  totalFunded: number;

  /** Processing fees on everything this host has live: what the balance is already holding up. */
  currentExposure: number;
  headroom: number;

  /**
   * The biggest single rental this host could still take on. The only number
   * here that means something without a paragraph of explanation, which is
   * why it is the one shown large.
   */
  maxBookingValue: number;

  /** Under a quarter of what they funded. Warned, not blocked -- blocking happens at zero, and finding out then is finding out too late. */
  isLow: boolean;
  /** Nothing left to cover anything with: no new bookings until they top up. */
  isExhausted: boolean;

  currencyCode: string;

  /** The processor's own rates, echoed so the calculator can work while somebody types rather than asking the server per keystroke. */
  processingFeePercent: number;
  processingFeeFixed: number;

  movements: IHostDepositMovement[];
}

/**
 * One thing that happened to the balance, and why.
 *
 * A ledger rather than a running number, because when a host asks where their
 * 200 EUR went, "you have 140" is not an answer.
 */
export interface IHostDepositMovement {
  id: number;
  /** Signed: positive put money in, negative took it out. */
  amount: number;
  /** Funded | CancellationCharge | ProcessingFeeKept | Refunded | Adjustment. */
  kind: string;
  reason?: string;
  /** The booking that caused it, on a cancellation charge. */
  bookingId?: number;
  createdAt: string;
}

/** What the client needs to confirm a top-up with Stripe.js -- same shape as any other payment in the app. */
export interface IHostDepositTopUp {
  clientSecret: string;
  amount: number;
  currencyCode: string;
}

/**
 * What the server says when a host cannot accept a booking because their
 * deposit will not stretch to it.
 *
 * Carries the figures, not just the refusal: "you cannot accept this" is a
 * dead end, while "you need 180 on deposit, add 45" is something a host can
 * act on before the customer loses interest.
 */
export interface IHostDepositShortfall {
  message: 'HostDepositInsufficient';
  requiredBalance: number;
  shortfallToFund: number;
  currentBalance: number;
  currencyCode: string;
}

/**
 * The biggest single rental a given balance would cover, worked out on the
 * client so the deposit calculator can answer while somebody is still typing.
 *
 * Mirrors HostExposureCalculator.MaxBookingValueFor exactly, rounding DOWN
 * for the same reason: this number is shown as a ceiling and then enforced by
 * the server, so rounding it up by a cent would advertise a booking the gate
 * refuses.
 */
export const maxBookingValueFor = (deposit: number, feePercent: number, feeFixed: number): number => {
  const afterFixed = deposit - feeFixed;
  if (afterFixed <= 0 || feePercent <= 0) return 0;
  return Math.floor((afterFixed / (feePercent / 100)) * 100) / 100;
};
